// R116 #2 — what a vendor (or anyone in the vendor's GROUP text) said back on a
// work order: ONE renderer for the Maintenance card's Vendor flow and the
// Calendar WO inspector. A reply names its sender (the vendor, a teammate, or
// the number — never "Guest"), says how it came in (text / group text / the
// vendor page), and shows the photos a text carried as thumbnails that open
// the shared Lightbox (videos open in a new tab — Quo media urls carry no
// extension the viewer could key on). Every value is escaped here.
import { Formatters } from './formatters';
import { openLightbox } from '../components/Lightbox';

export interface VendorReplyMedia { url: string; type?: string }
export interface VendorReply {
  at?: string; from?: string; text?: string; via?: string;
  media?: VendorReplyMedia[]; from_kind?: string; group_n?: number; fanned?: number;
}

const esc = (s: unknown): string => Formatters.escapeHtml(s == null ? '' : String(s));
const VIA: Record<string, string> = { sms: 'text', sms_group: 'group text', link: 'vendor page' };
const KIND: Record<string, string> = { vendor: 'vendor', staff: 'team', typed: '', unknown: '' };
const isHttp = (u: string) => /^https?:\/\//i.test(u);
const isVideo = (m: VendorReplyMedia) => /^video\//i.test(String(m.type || '')) || /\.(mp4|mov|m4v|webm|3gp)(\?|#|$)/i.test(m.url);

function when(at: string | undefined): string {
  const ms = Date.parse(String(at || ''));
  if (!Number.isFinite(ms)) return '';
  return new Date(ms).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/Los_Angeles' });
}

const CSS_ID = 'vrx-css';
const CSS = `
.vrx { display:flex; flex-direction:column; gap:8px; margin-top:6px; }
.vrx-row { border:1px solid var(--border, #e5e9f2); border-radius:var(--radius-sm, 8px); background:var(--card, #fff); padding:8px 10px; }
.vrx-h { display:flex; align-items:center; gap:6px; flex-wrap:wrap; font-size:11.5px; color:var(--text-muted, #64748b); }
.vrx-who { font-weight:700; color:var(--text, #0f172a); font-size:12px; }
.vrx-kind { font-size:10.5px; font-weight:600; padding:1px 8px; border-radius:var(--radius-pill, 999px); background:var(--bg-subtle, #f8fafc); border:1px solid var(--border, #e5e9f2); color:var(--text-muted, #64748b); }
.vrx-kind.vendor { background:var(--accent-soft, #eaf2ff); border-color:var(--accent-border, #bfd7fe); color:var(--accent, #2563eb); }
.vrx-text { font-size:12.5px; color:var(--text, #0f172a); line-height:1.45; margin-top:4px; white-space:pre-wrap; overflow-wrap:anywhere; }
.vrx-media { display:flex; flex-wrap:wrap; gap:6px; margin-top:6px; }
.vrx-th { width:64px; height:64px; padding:0; border:1px solid var(--border, #e5e9f2); border-radius:var(--radius-sm, 8px); overflow:hidden; background:var(--bg-subtle, #f8fafc); cursor:zoom-in; display:inline-flex; align-items:center; justify-content:center; text-decoration:none; color:var(--text-muted, #64748b); font-size:20px; }
.vrx-th img { width:100%; height:100%; object-fit:cover; display:block; }
.vrx-th:focus-visible { outline:2px solid var(--accent, #2563eb); outline-offset:1px; }
.vrx-vid { cursor:pointer; flex-direction:column; font-size:18px; gap:2px; }
.vrx-vid small { font-size:10px; font-weight:600; }
.vrx-more { font-size:11.5px; color:var(--text-faint, #94a3b8); }
`;
let bound = false;
function ensure(): void {
  if (typeof document === 'undefined') return;
  if (!document.getElementById(CSS_ID)) {
    const st = document.createElement('style'); st.id = CSS_ID; st.textContent = CSS; document.head.appendChild(st);
  }
  if (bound) return;
  bound = true;
  // ONE delegated listener: the strips are re-rendered by innerHTML on every page redraw.
  document.addEventListener('click', (e) => {
    const t = (e.target as HTMLElement | null)?.closest?.('[data-vrx-src]') as HTMLElement | null;
    if (!t) return;
    e.preventDefault();
    const strip = t.closest('.vrx') || t.parentElement;
    const all = Array.from((strip || document).querySelectorAll<HTMLElement>('[data-vrx-src]'));
    const urls = all.map(x => x.dataset.vrxSrc || '').filter(Boolean);
    const caps = all.map(x => x.dataset.vrxCap || '');
    openLightbox(urls, Math.max(0, all.indexOf(t)), { caption: (i) => caps[i] || '' });
  });
}

/** The newest `limit` replies (oldest first), each with its sender, channel, time, text and photo thumbnails. */
export function vendorRepliesHtml(replies: VendorReply[] | null | undefined, o: { limit?: number } = {}): string {
  const list = (Array.isArray(replies) ? replies : []).filter(Boolean);
  if (!list.length) return '';
  ensure();
  const limit = Math.max(1, o.limit || 4);
  const shown = list.slice(-limit);
  const rows = shown.map(r => {
    const media = (Array.isArray(r.media) ? r.media : []).filter(m => m && isHttp(String(m.url || '')));
    const who = String(r.from || '').trim() || 'Unknown number';
    const kind = KIND[String(r.from_kind || '')] ?? '';
    const via = VIA[String(r.via || '')] || String(r.via || '');
    const cap = `${who}${r.at ? ` · ${when(r.at)}` : ''}`;
    const thumbs = media.map((m, i) => isVideo(m)
      ? `<a class="vrx-th vrx-vid" href="${esc(m.url)}" target="_blank" rel="noopener" title="Open the video ${who} sent"><span>▶</span><small>video</small></a>`
      : `<button type="button" class="vrx-th" data-vrx-src="${esc(m.url)}" data-vrx-cap="${esc(cap)}" aria-label="Photo ${i + 1} from ${esc(who)} — open"><img src="${esc(m.url)}" alt="" loading="lazy"></button>`).join('');
    return `<div class="vrx-row">
      <div class="vrx-h"><span class="vrx-who">${esc(who)}</span>${kind ? `<span class="vrx-kind ${esc(r.from_kind)}">${esc(kind)}</span>` : ''}<span>${esc(via)}${r.group_n ? ` · ${esc(r.group_n)} people` : ''}${r.at ? ` · ${esc(when(r.at))}` : ''}</span>${r.fanned ? `<span class="vrx-more" title="A text can't say which request it answers — it is on every open request this vendor holds">· on ${esc(r.fanned)} requests</span>` : ''}</div>
      ${r.text ? `<div class="vrx-text">${esc(r.text)}</div>` : ''}
      ${thumbs ? `<div class="vrx-media">${thumbs}</div>` : ''}
    </div>`;
  }).join('');
  const more = list.length > shown.length ? `<div class="vrx-more">${list.length - shown.length} earlier message${list.length - shown.length === 1 ? '' : 's'} not shown</div>` : '';
  return `<div class="vrx">${more}${rows}</div>`;
}

/** The "texted …" chip text: who the work order went to (a group says how many). */
export function vendorTextedLabel(vt: any): string {
  if (!vt) return '';
  const day = String(vt.at || '').slice(0, 10);
  if (vt.group && vt.group.n) return `texted ${day} → group of ${vt.group.n}`;
  return `texted ${day}${vt.to ? ` → ${vt.to}` : ''}`;
}
